import { Component, EventEmitter, Input, Inject, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, finalize, mergeMap, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import * as moment from 'moment';

import { TapeChartWikiService } from '@app/_components/tape-chart-wiki/tape-chart-wiki.service';
import { AvailableWindow, QuoteContext, QuoteContextService } from '@app/_components/quote/quote-context.service';
import { QuoteService } from '@app/_components/quote/quote.service';
import { QuoteComponent } from '@app/_components/quote/quote/quote.component';
import { QuoteSource } from '@app/_components/quote/quote-source.enum';
import { BflHelpDialogComponent } from '@app/_components/best-fit/bfl-help-dialog/bfl-help-dialog.component';

/**
 * BestFitListComponent — the standalone flex-date finder table.
 *
 * Two modes:
 *  • Embedded  — parent (tape-chart-wiki-list) passes [tapeChartData] and
 *                [quoteContextInput]; component renders the fw-table and
 *                emits (windowPicked) when the reservationist selects a row,
 *                so the tape-chart grid can highlight the property.
 *  • Standalone — accessed via /best-fit-list route; renders its own search
 *                form (date range, flexible-nights, resort, bedrooms) and
 *                opens the Quote dialog directly.
 */
@Component({
    selector: 'app-best-fit-list',
    templateUrl: './best-fit-list.component.html',
    styleUrls: ['./best-fit-list.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class BestFitListComponent implements OnInit, OnChanges {

    // ── Embedded-mode inputs (provided by tape-chart-wiki-list) ─────────────
    /** Non-null signals embedded mode — component skips its own search form. */
    @Input() tapeChartData: any[] | null = null;
    @Input() quoteContextInput: QuoteContext | null = null;
    /** Night counts selected by the reservationist in the tape-chart search bar. */
    @Input() flexNightsList: number[] = [];
    @Input() flexSearchingInput = false;
    @Input() msgOrigin: any = null;
    @Input() msgRequest: any = null;

    /** Fires when a single row is picked. Parent uses this to highlight the tape-chart grid row. */
    @Output() windowPicked = new EventEmitter<AvailableWindow>();

    /** Fires after a quote is successfully sent so the parent dialog (inbox)
     *  can show the success toast — best-fit closes itself immediately after,
     *  destroying its own scoped MessageService before the toast could render. */
    @Output() quoteAdded = new EventEmitter<any>();

    get isEmbedded(): boolean { return this.tapeChartData !== null; }

    // ── Internal data ────────────────────────────────────────────────────────
    private _tapeChartsByProperty: any[] = [];
    private _quoteContext: QuoteContext | null = null;
    currentSearchForm: any = {};

    // ── Standalone search (delegated to app-search-template) ────────────────
    /** Reference to the shared search-template component (same one used by tape chart) */
    @ViewChild('bflSearchTpl') bflSearchTpl: any;

    searching = false;
    searchError: string | null = null;
    flexibleMode = false;
    selectedNights: number[] = [];

    /** Pre-fill values passed to app-search-template via @Input bindings */
    prefilledStartDate: any = null;
    prefilledEndDate:   any = null;
    prefilledNights:    number[] = [];

    // ── Flex-window state ────────────────────────────────────────────────────
    flexWindows: AvailableWindow[] = [];
    flexWindowsLimit = 50;
    flexSearching = false;

    selectedFlexKeys = new Set<string>();

    flexSortField = 'fit';
    flexSortDir: 'asc' | 'desc' = 'desc';

    flexGlobalFilter   = '';
    flexFilterFit      = '';
    flexFilterBed      = '';
    flexFilterBath     = '';
    flexFilterView     = '';
    flexFilterProp     = '';
    flexFilterCheckin  = '';
    flexFilterCheckout = '';
    flexFilterNights   = '';

    private thumbCache = new Map<number, string | null>();

    constructor(
        private tapeChartWikiService: TapeChartWikiService,
        private quoteContextService: QuoteContextService,
        private quoteService: QuoteService,
        private dialog: MatDialog,
        private messageService: MessageService,
        @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
        @Optional() private selfDialogRef: MatDialogRef<BestFitListComponent>
    ) {}

    ngOnInit(): void {
        // When opened as a MatDialog from the inbox, extract comm-tool context
        // so that every Quote dialog opened from here carries the thread linkage
        // (Parse button, reply-to envelope) and guest email for GMP auto-lookup.
        if (this.dialogData?.msgOrigin  != null) { this.msgOrigin  = this.dialogData.msgOrigin;  }
        if (this.dialogData?.msgRequest != null) { this.msgRequest = this.dialogData.msgRequest; }

        // Pre-fill and auto-search from the Smart-Parsed quote context
        if (this.dialogData?.quoteContext) {
            this.applyDialogContext(this.dialogData.quoteContext);
        }
    }

    /** Pre-fills the search template from an inbox QuoteContext and auto-triggers search. */
    private applyDialogContext(ctx: QuoteContext): void {
        const start = ctx.flexRangeStart ?? ctx.checkInDate ?? new Date();
        const end   = ctx.flexRangeEnd   ?? ctx.checkOutDate ?? (() => {
            const d = new Date(); d.setDate(d.getDate() + 60); return d;
        })();

        // Pass dates to the search template via @Input bindings
        this.prefilledStartDate = start;
        this.prefilledEndDate   = end;

        if (ctx.flexMode) {
            this.flexibleMode = true;
            const nights = (ctx as any).flexNightsList?.length
                ? [...(ctx as any).flexNightsList]
                : (ctx.flexNumNights ? [ctx.flexNumNights] : []);
            this.selectedNights   = nights;
            this.prefilledNights  = [...nights];
        }

        const canAutoSearch = ctx.flexMode
            ? this.selectedNights.length > 0
            : !!(start && end);

        if (canAutoSearch) {
            // Wait for the search template (and its developments list) to be ready
            setTimeout(() => this.applyBflSearchFilters(ctx), 50);
        }
    }

    /** Retries until app-search-template is rendered and developments are loaded,
     *  then patches resort + bedrooms and fires searchClick(). */
    private applyBflSearchFilters(ctx: QuoteContext, attempt = 0): void {
        const tpl = this.bflSearchTpl;
        if (!tpl || !tpl.form1) {
            if (attempt < 20) { setTimeout(() => this.applyBflSearchFilters(ctx, attempt + 1), 200); }
            return;
        }

        const resortHint = ctx.resortLabel ?? ctx.resortCode ?? null;
        if (resortHint) {
            const devsLoaded = !!(tpl['developments'] && tpl['developments'].length);
            if (!devsLoaded && attempt < 20) {
                setTimeout(() => this.applyBflSearchFilters(ctx, attempt + 1), 200);
                return;
            }
            const lower = resortHint.toLowerCase();
            const devs  = tpl['developments'] || [];
            const matches = devs.filter((d: any) =>
                (d.addressDevelopmentName || '').toLowerCase().includes(lower)
            );
            if (matches.length) {
                tpl.form1.patchValue({ formDevelopmentIds: matches.map((d: any) => d.developmentId) });
            }
        }

        if (ctx.bedrooms != null) {
            tpl.form1.patchValue({ formBedrooms: [ctx.bedrooms] });
        }

        tpl.searchClick();
    }

    /** Called by app-search-template's (doSearchCB) — same event used by the tape chart */
    onSearchTemplateSubmit(searchForm: any): void {
        const startDate  = searchForm.startDate as Date;
        const endDate    = searchForm.endDate   as Date;
        if (!startDate || !endDate) { return; }

        const diffNights = Math.round((endDate.getTime() - startDate.getTime()) / 86400000);

        // Capture nights from the template's LOS multiselect (shown when flexibleMode=true)
        if (searchForm.numNtsList?.length) {
            this.selectedNights = [...searchForm.numNtsList];
        }

        if (this.flexibleMode && !this.selectedNights.length) {
            this.messageService.add({
                severity: 'warn', summary: 'Select nights',
                detail: 'Choose at least one night count for flexible search.'
            });
            return;
        }

        const devIds:       number[] = searchForm.developmentIdList    ?? [];
        const bedList:      number[] = searchForm.bedroomList          ?? [];
        const viewList:     string[] = searchForm.viewList             ?? [];
        const strIds:       number[] = searchForm.shortTermRentalIdList ?? [];
        const locationIds:  number[] = searchForm.locationIdList        ?? [];
        const sleepList:    number[] = searchForm.sleepList             ?? [];
        const listingTypes: number[] = searchForm.listingTypeList       ?? [];

        // Carry guest identity forward from the Smart-Parsed inbox context so
        // QuoteComponent can auto-select (or create) the Guest Master Profile.
        const inboxCtx = this.dialogData?.quoteContext as QuoteContext | null;

        this._quoteContext = {
            flexMode:       this.flexibleMode,
            flexNumNights:  this.flexibleMode ? (this.selectedNights[0] ?? diffNights) : diffNights,
            flexRangeStart: startDate,
            flexRangeEnd:   endDate,
            checkInDate:    this.flexibleMode ? null : startDate,
            checkOutDate:   this.flexibleMode ? null : endDate,
            bedrooms:       bedList.length === 1 ? bedList[0] : null,
            resortCode:     null,
            resortKeywords: [],
            numAdults:      inboxCtx?.numAdults   ?? 2,
            numChildren:    inboxCtx?.numChildren ?? 0,
            guestName:      inboxCtx?.guestName   ?? null,
            guestEmail:     inboxCtx?.guestEmail  ?? null,
            guestPhone:     inboxCtx?.guestPhone  ?? null,
            resortLabel:    null
        } as any as QuoteContext;

        // Preserve the shared resolvedGmpId across multiple Quote openings so
        // the second dialog doesn't prompt to create a duplicate GMP.
        if (inboxCtx?.resolvedGmpId) {
            (this._quoteContext as any).resolvedGmpId = inboxCtx.resolvedGmpId;
        }

        if (this.flexibleMode) {
            (this._quoteContext as any).flexNightsList = [...this.selectedNights];
        }

        this.currentSearchForm = { startDate, endDate };

        // ── Request payload — mirror the OLD tape-chart 'Dates' mode exactly ──
        //
        // When the OLD tape-chart was opened from the inbox with a flex quote
        // context it auto-selected `selectedRateOption = 'dates'` which produced
        // these backend params in getTapeChartDetails() (commit 6c43d2a9^):
        //
        //      searchByRateLos     = true      // 'dates' mode => LOS join query
        //      showAvailableOnly   = '0'       // never pre-filter; rely on findAvailableWindows
        //      searchWithLosNights = '0'       // forced to 0 whenever searchByRateLos=true
        //
        // The TapeChartBL query picks the WITH-LOS branch (SELECT including
        // rdlos.* + `yieldPriceAMT` CASE + `isMaintenance`) which expands rate
        // rows across the full ±10-day range and applies the auto-coupon
        // post-processing.  That's what produced 800+ windows in the old flow.
        //
        // We also pass the Dates as Date objects (with timezone offset applied)
        // rather than pre-formatted 'YYYY-MM-DD' strings, matching the OLD
        // `addTimeOffsetToLocalDateTime` path so the backend's LocalDate
        // deserializer sees the same value the user picked (no UTC drift).
        const reqForm: any = {
            startDate:           this.addTimeOffsetToLocalDate(startDate),
            endDate:             this.addTimeOffsetToLocalDate(endDate),
            searchByRateLos:     true,
            showAvailableOnly:   '0',
            searchWithLosNights: '0'
        };
        if (devIds.length)       { reqForm.developmentIdList     = devIds; }
        if (bedList.length)      { reqForm.bedroomList           = bedList; }
        if (viewList.length)     { reqForm.viewList              = viewList; }
        if (strIds.length)       { reqForm.shortTermRentalIdList = strIds; }
        if (locationIds.length)  { reqForm.locationIdList        = locationIds; }
        if (sleepList.length)    { reqForm.sleepList             = sleepList; }
        if (listingTypes.length) { reqForm.listingTypeList       = listingTypes; }

        this.flexWindows = [];
        this.selectedFlexKeys.clear();
        this.searching   = true;
        this.searchError = null;

        this.tapeChartWikiService.searchRateSummaries(reqForm).subscribe(
            res => {
                this.searching = false;
                this._tapeChartsByProperty = this.buildPropertyRows(res?.data?.rates ?? []);
                this.runFlexFinder();
            },
            err => {
                this.searching   = false;
                this.searchError = 'Search failed. Please try again.';
                console.error('BestFitList search error:', err);
            }
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.isEmbedded) { return; }

        if (changes['quoteContextInput']) {
            this._quoteContext = this.quoteContextInput;
        }
        if (changes['tapeChartData']) {
            this._tapeChartsByProperty = this.tapeChartData || [];
        }
        // Re-scan whenever data or context arrives (both must be present)
        if ((changes['tapeChartData'] || changes['quoteContextInput'] || changes['flexNightsList'])
            && this._tapeChartsByProperty.length && this._quoteContext) {
            this.runFlexFinder();
        }
    }

    /** Groups raw API rate rows by propertyCode AND de-duplicates within each
     *  property by `analyzedDate` — same behaviour as the OLD tape-chart's
     *  `matchHeaderDate()` which tracked `previousMatchedDate` to skip repeat
     *  rows for the same day.
     *
     *  Why dedup is mandatory: the backend SQL LEFT JOINs
     *  `str_maintenance_event_short_term_rental` + `str_maintenance_event`.
     *  A property with multiple maintenance events produces N joined rows per
     *  `str_rate` row; `SELECT DISTINCT` collapses rows where every selected
     *  column matches, but the per-row `isMaintenance` CASE depends on whether
     *  `r.analyzedDate` falls inside a *specific* event's date range — so on a
     *  day that overlaps any maint event, we get 2 rows for that analyzedDate
     *  (one with `isMaintenance='true'`, one with `'false'`) and DISTINCT
     *  keeps both.
     *
     *  `findAvailableWindows()` does a positional sliding-window scan that
     *  expects day N, N+1, N+2, … on consecutive positions; duplicates of the
     *  same day break the continuity check and make every window that touches
     *  a maint-affected day get rejected. That's what reduced 800+ windows
     *  down to ~40 in the standalone flow.
     *
     *  On conflict we prefer `isMaintenance==='true'` so the UI can still
     *  surface the maintenance flag on the affected day. */
    private buildPropertyRows(rates: any[]): any[] {
        const map = new Map<string, any>();
        for (const rate of rates) {
            const key = rate.propertyCode;
            if (!map.has(key)) {
                map.set(key, {
                    propertyCode:           key,
                    addressDevelopmentName: rate.addressDevelopmentName,
                    bedrooms:               rate.bedrooms,
                    bathrooms:              rate.bathrooms,
                    viewType:               rate.viewType,
                    listingStatus:          rate.listingStatus,
                    shortTermRentalId:      rate.shortTermRentalId,
                    _ratesByDate:           new Map<string, any>(),   // ← dedup index
                    rates:                  []
                });
            }
            const prop = map.get(key);
            const dateKey = String(rate.analyzedDate);
            const existing = prop._ratesByDate.get(dateKey);
            if (!existing) {
                prop._ratesByDate.set(dateKey, rate);
            } else {
                // Keep the "under maintenance" variant when duplicated by the
                // maintenance-event LEFT JOIN fan-out.
                const existingMx = existing?.isMaintenance === true || existing?.isMaintenance === 'true';
                const incomingMx = rate?.isMaintenance     === true || rate?.isMaintenance     === 'true';
                if (!existingMx && incomingMx) {
                    prop._ratesByDate.set(dateKey, rate);
                }
            }
        }
        // Materialize deduped rates, drop the scratch index
        return Array.from(map.values()).map(p => {
            p.rates = Array.from((p._ratesByDate as Map<string, any>).values());
            delete p._ratesByDate;
            return p;
        });
    }

    // ────────────────────────────────────────────────────────────────────────
    //  Flex finder
    // ────────────────────────────────────────────────────────────────────────

    runFlexFinder(): void {
        const ctx = this._quoteContext;
        if (!ctx) { this.flexWindows = []; this.flexSearching = false; return; }

        // ── Sliding window scan — all modes (flex, non-flex standalone, embedded) ─
        //
        // The old embedded tape-chart always sent showAvailableOnly:'0' and let
        // findAvailableWindows determine availability day-by-day (via reservationId).
        // We replicate that here for ALL modes — the shortcut of "one window per
        // available property" is gone because it required showAvailableOnly:'1' and
        // prevented scanning properties that have gaps *around* existing bookings.
        //
        // Night counts to scan:
        //   • Embedded   — night list from tape-chart parent (@Input flexNightsList)
        //   • Flex standalone — night pills selected by the user
        //   • Non-flex standalone — exactly diffNights (check-in to check-out span)
        const nightsToScan: number[] = this.isEmbedded
            ? (this.flexNightsList.length ? this.flexNightsList
                : (ctx.flexNumNights ? [ctx.flexNumNights] : []))
            : (this.flexibleMode
                ? (this.selectedNights.length ? [...this.selectedNights]
                    : (ctx.flexNumNights ? [ctx.flexNumNights] : []))
                : [ctx.flexNumNights ?? 0]);   // non-flex: scan for exactly diffNights

        if (!nightsToScan.length || !nightsToScan[0]) {
            this.flexWindows = []; this.flexSearching = false; return;
        }

        // For non-flex, rangeStart/End = the exact check-in/out dates so the scanner
        // only surfaces windows that start exactly on (or within) those dates.
        const rangeStart = ctx.flexRangeStart ?? ctx.checkInDate;
        const rangeEnd   = ctx.flexRangeEnd   ?? ctx.checkOutDate;
        if (!rangeStart || !rangeEnd) { this.flexWindows = []; this.flexSearching = false; return; }

        const allWindows = nightsToScan.flatMap(n =>
            this.quoteContextService.findAvailableWindows(
                this._tapeChartsByProperty, n,
                rangeStart, rangeEnd,
                ctx.resortCode   ?? undefined,
                ctx.resortKeywords,
                ctx.bedrooms
            )
        );

        this.flexWindows = allWindows;
        this.selectedFlexKeys.clear();
        this.flexSortField = 'fit';
        this.flexSortDir   = 'desc';
        this.flexSearching = false;

        // Sort immediately by gapScore-based effectiveScore so the initial
        // display is already in the correct order before pricing arrives.
        this.applyFlexTableSort();

        this.fetchFlexWindowTotals(ctx);
        this.fetchFlexThumbnails();
    }

    fetchFlexWindowTotals(ctx: QuoteContext): void {
        if (!this.flexWindows?.length) { return; }
        const submitted = this.flexWindows.filter(w => w.shortTermRentalId != null);
        if (!submitted.length) { return; }

        const BATCH_SIZE   = 100;
        const MAX_PARALLEL = 4;
        interface Batch { windows: AvailableWindow[]; payload: any[]; }

        const batches: Batch[] = [];
        for (let i = 0; i < submitted.length; i += BATCH_SIZE) {
            const windows = submitted.slice(i, i + BATCH_SIZE);
            batches.push({
                windows,
                payload: windows.map(w => ({
                    shortTermRentalId:  w.shortTermRentalId,
                    checkInDate:        moment(w.startDate).format('YYYY-MM-DD'),
                    checkOutDate:       moment(w.endDate).format('YYYY-MM-DD'),
                    numberOfNightsStay: w.numNights,
                    numAdults:          ctx?.numAdults   ?? 2,
                    numChildren:        ctx?.numChildren ?? 0,
                    numPets:            0,
                    couponCode:         '',
                    inquirerIpAddress:  ''
                }))
            });
        }

        from(batches).pipe(
            mergeMap((batch: Batch) =>
                this.quoteService.bulkTotals(batch.payload).pipe(
                    tap((res: any) => {
                        const items = res?.data?.bulkQuoteTotals?.items || [];
                        for (let i = 0; i < items.length; i++) {
                            const match = batch.windows[i];
                            if (!match) { continue; }
                            const item = items[i];
                            if (item.status === 'OK') {
                                match.dbGrandTotal             = item.grandTotal             ?? null;
                                match.dbGrandTotalWithTax      = item.grandTotalWithTax      ?? null;
                                match.dbPerNightAvg            = item.perNightAvg            ?? null;
                                match.dbPostCouponNightlyRate  = item.postCouponNightlyRate  ?? null;
                                match.pricingStatus       = 'ok';
                                match.pricingError        = null;
                                if (item.fitScore != null) {
                                    match.fitScore       = item.fitScore;
                                    match.fitReason      = item.fitReason      ?? null;
                                    match.fitReasonLabel = item.fitReasonLabel ?? null;
                                }
                            } else {
                                match.pricingStatus = 'error';
                                match.pricingError  = item.errorMessage || 'Pricing unavailable';
                            }
                        }
                        // Re-sort after each batch so rows settle into their
                        // correct position progressively rather than jumping
                        // all at once when the last batch completes.
                        this.applyFlexTableSort();
                    }),
                    catchError((err: any) => {
                        console.warn('bulk-totals batch failed:', err);
                        batch.windows.forEach(w => {
                            if (w.pricingStatus === 'pending') {
                                w.pricingStatus = 'error';
                                w.pricingError  = 'Live pricing unavailable — showing estimate.';
                            }
                        });
                        this.flexWindows = [...this.flexWindows];
                        return of(null);
                    })
                ),
                MAX_PARALLEL
            ),
            finalize(() => {
                this.applyFlexTableSort();
                this.flexWindows.forEach(w => {
                    if (w.pricingStatus === 'pending') { w.pricingStatus = 'estimate'; }
                });
            })
        ).subscribe();
    }

    private fetchFlexThumbnails(): void {
        // Step 1 — restore already-cached URLs onto the fresh window objects
        this.flexWindows.forEach(w => {
            if (w.shortTermRentalId != null && this.thumbCache.has(+w.shortTermRentalId)) {
                w.thumbImg = this.thumbCache.get(+w.shortTermRentalId) ?? null;
            }
        });

        // Step 2 — fetch only uncached IDs
        const uniqueIds: number[] = [...new Set(
            this.flexWindows
                .map(w => w.shortTermRentalId)
                .filter(id => id != null && !this.thumbCache.has(+id))
        )].map(id => +id).slice(0, 200);

        if (!uniqueIds.length) { this.flexWindows = [...this.flexWindows]; return; }

        from(uniqueIds).pipe(
            mergeMap(strId =>
                this.quoteService.findPictureByPropertyId(strId).pipe(
                    tap((res: any) => {
                        const fn  = res?.data?.picture?.fileName ?? null;
                        const url = fn ? `https://thumb.kbmhawaii.com/${fn}` : null;
                        this.thumbCache.set(strId, url);
                        this.flexWindows.forEach(w => {
                            if (w.shortTermRentalId != null && +w.shortTermRentalId === strId) {
                                w.thumbImg = url;
                            }
                        });
                    }),
                    catchError(() => { this.thumbCache.set(strId, null); return of(null); })
                ),
                8
            ),
            finalize(() => { this.flexWindows = [...this.flexWindows]; })
        ).subscribe();
    }

    // ────────────────────────────────────────────────────────────────────────
    //  Fit scoring
    // ────────────────────────────────────────────────────────────────────────

    fitTier(win: AvailableWindow): 0 | 1 | 2 {
        const s = win.fitScore;
        if (s != null) {
            if (s >= 75) { return 2; }
            if (s >= 45) { return 1; }
            return 0;
        }
        return (win.gapScore ?? 0) as 0 | 1 | 2;
    }

    private effectiveScore(win: AvailableWindow): number {
        if (win.fitScore != null) { return win.fitScore; }
        const gs = win.gapScore ?? 0;
        return gs === 2 ? 60 : gs === 1 ? 35 : 15;
    }

    // ────────────────────────────────────────────────────────────────────────
    //  Sort
    // ────────────────────────────────────────────────────────────────────────

    sortFlexBy(field: string): void {
        if (this.flexSortField === field) {
            this.flexSortDir = this.flexSortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.flexSortField = field;
            this.flexSortDir   = ['fit', 'total', 'perNight'].includes(field) ? 'desc' : 'asc';
        }
        this.applyFlexTableSort();
    }

    private applyFlexTableSort(): void {
        this.flexWindows = [...this.flexWindows].sort((a, b) => {
            let diff = 0;
            switch (this.flexSortField) {
                case 'fit': {
                    const sd = this.effectiveScore(a) - this.effectiveScore(b);
                    if (sd !== 0) { diff = sd; break; }
                    return (new Date(a.startDate).getTime() - new Date(b.startDate).getTime()) ||
                           (new Date(a.endDate).getTime()   - new Date(b.endDate).getTime());
                }
                case 'checkin':   diff = new Date(a.startDate).getTime() - new Date(b.startDate).getTime(); break;
                case 'checkout':  diff = new Date(a.endDate).getTime()   - new Date(b.endDate).getTime();   break;
                case 'nights':    diff = a.numNights - b.numNights;                                         break;
                case 'gapBefore': diff = a.gapNightsBefore - b.gapNightsBefore;                             break;
                case 'gapAfter':  diff = a.gapNightsAfter  - b.gapNightsAfter;                              break;
                case 'perNight':  diff = (a.dbPerNightAvg ?? a.perNightFromPrice ?? Infinity) -
                                         (b.dbPerNightAvg ?? b.perNightFromPrice ?? Infinity);              break;
                case 'total':     diff = (a.dbGrandTotalWithTax ?? a.preTaxSubtotal ?? Infinity) -
                                         (b.dbGrandTotalWithTax ?? b.preTaxSubtotal ?? Infinity);           break;
                case 'prop':      diff = (a.propertyCode || '').localeCompare(b.propertyCode || '');        break;
                case 'bed':       diff = (a.bedrooms  ?? Infinity) - (b.bedrooms  ?? Infinity);             break;
                case 'bath':      diff = (a.bathrooms ?? Infinity) - (b.bathrooms ?? Infinity);             break;
                case 'view':      diff = (this.viewTypeLabel(a.viewType) || '')
                                            .localeCompare(this.viewTypeLabel(b.viewType) || '');           break;
            }
            return this.flexSortDir === 'asc' ? diff : -diff;
        });
    }

    // ────────────────────────────────────────────────────────────────────────
    //  Column filters
    // ────────────────────────────────────────────────────────────────────────

    get flexUniqueBeds(): number[] {
        const s = new Set<number>();
        this.flexWindows.forEach(w => { if (w.bedrooms  != null) { s.add(Number(w.bedrooms)); } });
        return Array.from(s).sort((a, b) => a - b);
    }
    get flexUniqueBaths(): number[] {
        const s = new Set<number>();
        this.flexWindows.forEach(w => { if (w.bathrooms != null) { s.add(Number(w.bathrooms)); } });
        return Array.from(s).sort((a, b) => a - b);
    }
    get flexUniqueViews(): string[] {
        const s = new Set<string>();
        this.flexWindows.forEach(w => { if (w.viewType) { s.add(w.viewType); } });
        return Array.from(s).sort();
    }
    get flexUniqueNights(): number[] {
        const s = new Set<number>();
        this.flexWindows.forEach(w => s.add(w.numNights));
        return Array.from(s).sort((a, b) => a - b);
    }

    clearFlexFilters(): void {
        this.flexGlobalFilter = this.flexFilterFit  = this.flexFilterBed  = this.flexFilterBath =
        this.flexFilterView   = this.flexFilterProp = this.flexFilterCheckin =
        this.flexFilterCheckout = this.flexFilterNights = '';
    }

    get hasFlexFilters(): boolean {
        return !!(this.flexGlobalFilter || this.flexFilterFit  || this.flexFilterBed  ||
                  this.flexFilterBath   || this.flexFilterView || this.flexFilterProp  ||
                  this.flexFilterCheckin || this.flexFilterCheckout || this.flexFilterNights);
    }

    get displayedFlexWindows(): AvailableWindow[] {
        let list = this.flexWindows;

        const gq = this.flexGlobalFilter.trim().toLowerCase();
        if (gq) {
            const tokens = gq.split(/\s+/).filter(t => t.length > 0);
            list = list.filter(w => {
                const ciStr  = this.formatDateForSearch(w.startDate);
                const coStr  = this.formatDateForSearch(w.endDate);
                const pricePN = w.dbPerNightAvg ?? w.perNightFromPrice;
                const total   = w.dbGrandTotalWithTax ?? w.preTaxSubtotal;
                return (
                    (w.propertyCode           || '').toLowerCase().includes(gq) ||
                    (w.addressDevelopmentName || '').toLowerCase().includes(gq) ||
                    (this.viewTypeLabel(w.viewType) || '').toLowerCase().includes(gq) ||
                    String(w.bedrooms  ?? '').includes(gq) ||
                    String(w.bathrooms ?? '').includes(gq) ||
                    ciStr.includes(gq) || coStr.includes(gq) ||
                    tokens.every(t => (ciStr + ' ' + coStr).includes(t)) ||
                    String(w.numNights).includes(gq) ||
                    (pricePN != null && String(Math.round(pricePN)).includes(gq)) ||
                    (total   != null && String(Math.round(total)).includes(gq))
                );
            });
        }

        if (this.flexFilterFit  !== '') { list = list.filter(w => this.fitTier(w) === +this.flexFilterFit); }
        if (this.flexFilterBed  !== '') { list = list.filter(w => w.bedrooms  === +this.flexFilterBed); }
        if (this.flexFilterBath !== '') { list = list.filter(w => w.bathrooms === +this.flexFilterBath); }
        if (this.flexFilterView !== '') { list = list.filter(w => w.viewType  === this.flexFilterView); }
        if (this.flexFilterProp !== '') {
            const pq = this.flexFilterProp.toLowerCase();
            list = list.filter(w => (w.propertyCode || '').toLowerCase().includes(pq) ||
                                    (w.addressDevelopmentName || '').toLowerCase().includes(pq));
        }
        if (this.flexFilterCheckin  !== '') {
            list = list.filter(w => this.formatDateForSearch(w.startDate).includes(this.flexFilterCheckin.toLowerCase()));
        }
        if (this.flexFilterCheckout !== '') {
            list = list.filter(w => this.formatDateForSearch(w.endDate).includes(this.flexFilterCheckout.toLowerCase()));
        }
        if (this.flexFilterNights !== '') {
            list = list.filter(w => w.numNights === +this.flexFilterNights);
        }
        return list;
    }

    // ────────────────────────────────────────────────────────────────────────
    //  Row selection
    // ────────────────────────────────────────────────────────────────────────

    flexWinKey(w: AvailableWindow): string {
        return `${w.shortTermRentalId}|${+new Date(w.startDate)}`;
    }
    isFlexSelected(w: AvailableWindow): boolean {
        return this.selectedFlexKeys.has(this.flexWinKey(w));
    }
    toggleFlexSelect(w: AvailableWindow, e: Event): void {
        e.stopPropagation();
        const k = this.flexWinKey(w);
        this.selectedFlexKeys.has(k) ? this.selectedFlexKeys.delete(k) : this.selectedFlexKeys.add(k);
    }
    get selectedFlexCount(): number { return this.selectedFlexKeys.size; }
    get selectedFlexList(): AvailableWindow[] {
        return this.flexWindows.filter(w => this.selectedFlexKeys.has(this.flexWinKey(w)));
    }
    get allFlexSelected(): boolean {
        const shown = this.displayedFlexWindows.slice(0, this.flexWindowsLimit);
        return shown.length > 0 && shown.every(w => this.selectedFlexKeys.has(this.flexWinKey(w)));
    }
    toggleSelectAllFlex(e: Event): void {
        e.stopPropagation();
        const shown = this.displayedFlexWindows.slice(0, this.flexWindowsLimit);
        if (this.allFlexSelected) {
            shown.forEach(w => this.selectedFlexKeys.delete(this.flexWinKey(w)));
        } else {
            shown.forEach(w => this.selectedFlexKeys.add(this.flexWinKey(w)));
        }
    }

    // ────────────────────────────────────────────────────────────────────────
    //  Quote dialog
    // ────────────────────────────────────────────────────────────────────────

    pickFlexWindow(win: AvailableWindow): void {
        // Let the tape-chart parent know so it can highlight the property row
        this.windowPicked.emit(win);
        this.currentSearchForm.startDate = new Date(win.startDate);
        this.currentSearchForm.endDate   = new Date(win.endDate);
        const property = this._tapeChartsByProperty.find(p => p.propertyCode === win.propertyCode) || null;
        this.openMultiWindowQuote([{
            propertyCode:      win.propertyCode,
            shortTermRentalId: win.shortTermRentalId,
            startDate:         new Date(win.startDate),
            endDate:           new Date(win.endDate),
            numNights:         win.numNights,
            property
        }]);
    }

    buildSelectedQuotes(): void {
        const wins = this.selectedFlexList;
        if (!wins.length) { return; }

        const selectedWindows = wins.map(win => ({
            propertyCode:      win.propertyCode,
            shortTermRentalId: win.shortTermRentalId,
            startDate:         new Date(win.startDate),
            endDate:           new Date(win.endDate),
            numNights:         win.numNights,
            property:          this._tapeChartsByProperty.find(p => p.propertyCode === win.propertyCode) || null
        }));

        const minStart = selectedWindows.reduce((a, w) => (!a || w.startDate < a ? w.startDate : a), null as Date | null);
        const maxEnd   = selectedWindows.reduce((a, w) => (!a || w.endDate   > a ? w.endDate   : a), null as Date | null);
        if (minStart) { this.currentSearchForm.startDate = minStart; }
        if (maxEnd)   { this.currentSearchForm.endDate   = maxEnd; }

        // Notify parent of all picked windows for grid highlighting
        wins.forEach(w => this.windowPicked.emit(w));

        this.openMultiWindowQuote(selectedWindows);
    }

    private openMultiWindowQuote(selectedWindows: any[]): void {
        const ctx         = this._quoteContext;
        const numAdults   = ctx?.numAdults   != null ? ctx.numAdults   : 2;
        const numChildren = ctx?.numChildren != null ? ctx.numChildren : 0;
        const guestInfo   = ctx ? { guestName: ctx.guestName, guestEmail: ctx.guestEmail, guestPhone: ctx.guestPhone } : null;

        const cfg = new MatDialogConfig();
        cfg.disableClose = false;
        cfg.autoFocus    = true;
        cfg.hasBackdrop  = true;
        cfg.maxWidth     = '120vw';
        cfg.width        = '85vw';
        cfg.data = {
            title: 'Quote',
            showCloseButton: true,
            quote: {
                checkInDate:        this.currentSearchForm.startDate,
                checkOutDate:       this.currentSearchForm.endDate,
                selectedProperties: selectedWindows.map(w => w.property).filter(Boolean),
                selectedWindows,
                numAdults,
                numChildren,
                guestInfo,
                msgOrigin:  this.msgOrigin,
                msgRequest: this.msgRequest
            },
            msgOrigin:    this.msgOrigin,
            msgRequest:   this.msgRequest,
            quoteContext: ctx,
            // Tag the quote source so the analytics dashboard can break down
            // volume by entry point. If we were opened from the inbox (selfDialogRef
            // is set) the user came via inbox → best-fit; otherwise they hit
            // /best-fit-list directly.
            source: this.selfDialogRef ? QuoteSource.INBOX_BEST_FIT : QuoteSource.BEST_FIT
        };

        const ref = this.dialog.open(QuoteComponent, cfg);
        ref.componentInstance.doAdd.subscribe((payload: any) => {
            // payload is { message, quoteId } — email is sent async.
            const message = typeof payload === 'string' ? payload : payload?.message;
            const quoteId = typeof payload === 'string' ? null  : payload?.quoteId;
            if (this.selfDialogRef) {
                // Opened as dialog (e.g. from inbox): close quote dialog first,
                // then bubble the full payload up — parent's MessageService survives
                // our closure and will show the toast + poll for delivery status.
                ref.close();
                this.quoteAdded.emit(payload);
                this.selfDialogRef.close();
            } else {
                // Standalone route page: register the toast BEFORE closing the
                // quote dialog — closing it first disrupts Angular's change
                // detection and swallows the notification.
                this.messageService.add({ severity: 'info', summary: '📨 Quote Saved', detail: message, life: 8000 });
                if (quoteId) {
                    setTimeout(() => {
                        this.quoteService.getEmailStatus(quoteId).subscribe((res: any) => {
                            if (res?.data?.emailStatus?.emailSendStatus === 4) {
                                const errorDetail = res?.data?.emailStatus?.emailSendStatusMsg
                                    || 'The email could not be delivered to the guest.';
                                this.messageService.add({
                                    severity: 'error', summary: '⚠️ Email Delivery Failed',
                                    detail: errorDetail, life: 0
                                });
                            }
                        });
                    }, 5000);
                }
                setTimeout(() => ref.close(), 100);
            }
        });
    }

    // ────────────────────────────────────────────────────────────────────────
    //  Help dialog
    // ────────────────────────────────────────────────────────────────────────

    /** Opens the "How to use" guide as a nested Angular Material dialog.
     *  MatDialog properly stacks nested dialogs above their parent. */
    openHelp(): void {
        const cfg = new MatDialogConfig();
        cfg.width        = '520px';
        cfg.maxHeight    = '85vh';
        cfg.autoFocus    = false;
        cfg.hasBackdrop  = true;
        cfg.disableClose = false;
        this.dialog.open(BflHelpDialogComponent, cfg);
    }

    // ────────────────────────────────────────────────────────────────────────
    //  Helpers
    // ────────────────────────────────────────────────────────────────────────

    viewTypeLabel(code: string | null | undefined): string | null {
        if (!code) { return null; }
        const map: Record<string, string> = {
            'OV': 'Ocean View',    'OF': 'Ocean Front',  'POV': 'Premium Ocean',
            'OP': 'Partial Ocean', 'PK': 'Peek-a-Boo',   'BV':  'Beach View',
            'BF': 'Beach Front',   'BP': 'Beach Partial',
            'GV': 'Golf View',     'GF': 'Golf Front',   'GP':  'Golf Partial',
            'MV': 'Mountain',      'PMV': 'Mtn Partial',
            'LV': 'Lake View',     'LF': 'Lake Front',   'LVP': 'Lake Partial',
            'RV': 'Resort View',   'GGV': 'Garden View', 'NV':  'No View',
            'DT': 'Downtown',      'CIT': 'City View',   'SK':  'Ski In/Out',
            'TRE': 'Treeline',     'VAL': 'Valley',      'HIL': 'Hills'
        };
        return map[code] ?? code;
    }

    private formatDateForSearch(d: Date | null): string {
        if (!d) { return ''; }
        const months = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
        const dt = new Date(d);
        return `${months[dt.getMonth()]} ${dt.getDate()} ${dt.getFullYear()}`;
    }

    /**
     * Shifts a local Date by its timezone offset so JSON serialization yields
     * the same calendar day the user picked, not the UTC-shifted date. This
     * matches the OLD tape-chart's `addTimeOffsetToLocalDateTime()` so the
     * backend's LocalDate deserializer sees Aug 1 when the user picked Aug 1,
     * regardless of negative UTC offsets (e.g., HST = UTC-10).
     */
    private addTimeOffsetToLocalDate(datetime: Date): Date {
        const offsetMin = datetime.getTimezoneOffset();
        return offsetMin > 0
            ? new Date(datetime.getTime() + (offsetMin * 60000))
            : new Date(datetime.getTime() - (offsetMin * 60000));
    }

    get activeContext(): QuoteContext | null { return this._quoteContext; }
}
